/**
 * PaymentSelectionModal Component
 * 
 * Modal for selecting billing cycle (monthly/yearly) before checkout.
 * Prevents dismissal to ensure user completes the selection process.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {string} props.planTier - Selected plan tier ('base' or 'premium')
 * @param {Function} props.onSelectBillingCycle - Callback with selected cycle
 * @param {Function} props.onCancel - Callback to cancel selection
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

function PaymentSelectionModal({ isOpen, planTier, onSelectBillingCycle, onCancel }) {
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  // Plan pricing
  const planPrices = {
    base: {
      monthly: { price: 19.99, display: '$19.99/month' },
      yearly: { price: 220, display: '$220/year', savings: 'Save $20' }
    },
    premium: {
      monthly: { price: 29.99, display: '$29.99/month' },
      yearly: { price: 330, display: '$330/year', savings: 'Save $30' }
    }
  };

  const currentPlan = planPrices[planTier] || planPrices.base;
  const planName = planTier === 'base' ? 'Base' : 'Premium';

  const handleProceed = () => {
    setIsProcessing(true);
    onSelectBillingCycle(selectedCycle);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Complete Your {planName} Plan Subscription
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 p-4">
          <p className="text-center text-gray-600">
            Select your billing cycle to continue
          </p>

          {/* Billing Cycle Options */}
          <div className="space-y-3">
            {/* Monthly Option */}
            <div 
              onClick={() => setSelectedCycle('monthly')}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedCycle === 'monthly' 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-300 hover:border-orange-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedCycle === 'monthly' ? 'border-orange-500' : 'border-gray-400'
                  }`}>
                    {selectedCycle === 'monthly' && (
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Monthly</div>
                    <div className="text-sm text-gray-600">Billed monthly</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg">{currentPlan.monthly.display}</div>
                </div>
              </div>
            </div>

            {/* Yearly Option */}
            <div 
              onClick={() => setSelectedCycle('yearly')}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all relative ${
                selectedCycle === 'yearly' 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-300 hover:border-orange-300'
              }`}
            >
              <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                {currentPlan.yearly.savings}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedCycle === 'yearly' ? 'border-orange-500' : 'border-gray-400'
                  }`}>
                    {selectedCycle === 'yearly' && (
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Yearly</div>
                    <div className="text-sm text-gray-600">Billed annually</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg">{currentPlan.yearly.display}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>💳 Secure Payment:</strong> You'll be redirected to our secure payment partner Stripe to complete your subscription.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={handleProceed}
              disabled={isProcessing}
              className="btn-bawarchi-primary flex-1"
            >
              {isProcessing ? 'Processing...' : 'Proceed to Payment'}
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500">
            By proceeding, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PaymentSelectionModal;
